import React, { useMemo, useState } from 'react';
import {
  BrioModal,
  BrioModalBody,
  BrioButton,
} from '../../components/BrioComponents';
import { parsePastedRows, buildXlsxLookups } from '../utils/projectItemsXlsx';

// Touch-friendly bulk paste. iPad can't do range clipboard / keyboard paste
// into the grid, but pasting into a <textarea> via the iOS Paste menu works.
// User copies a block from Numbers/Excel, pastes here, we parse + append.
function PasteRowsModal({ versions, zonesById, onClose, onImport }) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const lookups = useMemo(
    () => buildXlsxLookups({ versions, zonesById }),
    [versions, zonesById]
  );

  const parsed = useMemo(() => {
    if (!text.trim()) return { rows: [], errors: [] };
    return parsePastedRows(text, lookups);
  }, [text, lookups]);

  const validRows = useMemo(() => parsed.rows.filter((r) => r.errors.length === 0), [parsed]);
  const invalidCount = parsed.rows.length - validRows.length;

  const handleApply = async () => {
    if (validRows.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    const inputs = validRows.map((r) => ({
      itemVersionId: r.itemVersionId,
      quantity: r.quantity,
      projectZoneId: r.projectZoneId,
      descriptionOverride: r.descriptionOverride,
      sequenceOrder: r.sequenceOrder,
    }));
    const result = await onImport(inputs);
    setSubmitting(false);
    if (result.success) onClose();
    else setSubmitError(result.error || 'Failed to add rows.');
  };

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={onClose} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleApply} disabled={validRows.length === 0 || submitting}>
        {submitting
          ? 'Adding…'
          : invalidCount > 0
            ? `Add ${validRows.length} valid (skip ${invalidCount})`
            : `Add ${validRows.length} row${validRows.length === 1 ? '' : 's'}`}
      </BrioButton>
    </>
  );

  return (
    <BrioModal title="Paste rows from Excel / Numbers" onClose={onClose} width="xl" footer={footer}>
      <BrioModalBody>
        {submitError && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>
            {submitError}
          </div>
        )}
        <p style={{ marginTop: 0, fontSize: '0.88rem' }}>
          Copy a block of cells from Numbers or Excel and paste it below. Columns, in order:
          &nbsp;<strong>Code</strong>, <strong>Quantity</strong>, <strong>Zone</strong> (optional), <strong>Description</strong> (optional).
          Tab- or comma-separated; a header row is detected and skipped.
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          placeholder={'MA.BRK.0001\t64\nPL.INT.0001\t620\nFL.VIT.0001\t185\tvilla-1'}
          style={{
            width: '100%',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            padding: '0.6rem',
            border: '1px solid var(--brio-border, #ccc)',
            borderRadius: 6,
            boxSizing: 'border-box',
          }}
          autoFocus
        />

        {parsed.rows.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              <strong>{parsed.rows.length}</strong> row{parsed.rows.length === 1 ? '' : 's'} parsed:
              &nbsp;<span style={{ color: '#2e7d32' }}>{validRows.length} valid</span>
              {invalidCount > 0 && <span>, <span style={{ color: '#a94442' }}>{invalidCount} with errors</span></span>}.
            </div>
            <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid var(--brio-border, #ccc)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--brio-surface-muted, #f7f7f5)' }}>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: '0.35rem 0.5rem', width: 30 }} />
                    <th style={{ padding: '0.35rem 0.5rem' }}>Code</th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>Qty</th>
                    <th style={{ padding: '0.35rem 0.5rem' }}>Zone</th>
                    <th style={{ padding: '0.35rem 0.5rem' }}>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.map((r) => {
                    const ok = r.errors.length === 0;
                    return (
                      <tr key={r.rowNumber} style={{ borderTop: '1px solid var(--brio-border-subtle, #eee)', background: ok ? 'transparent' : '#fdecec' }}>
                        <td style={{ padding: '0.3rem 0.5rem', color: ok ? '#2e7d32' : '#a94442' }}>{ok ? '✓' : '✗'}</td>
                        <td style={{ padding: '0.3rem 0.5rem', fontFamily: 'monospace' }}>{r.code}</td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{r.quantity ?? '—'}</td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>{r.zonePath || '—'}</td>
                        <td style={{ padding: '0.3rem 0.5rem', color: '#a94442', fontSize: '0.78rem' }}>{r.errors.join(' · ')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </BrioModalBody>
    </BrioModal>
  );
}

export default PasteRowsModal;
